import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import {
  Input,
  Button,
  Spinner,
  Select,
  SelectItem,
  Divider,
  Tabs,
  Tab,
  Card,
} from "@heroui/react";
import { Textarea } from "@heroui/input";
import { getCallData, postCallData, CallData } from "../api/api";
import { useParams } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";

type Color = "default" | "success" | "danger"

const DataInput: React.FC = () => {
  const { id: callid } = useParams();
  const [data, setData] = useState<CallData>();
  const [loading, setLoading] = useState<boolean>(true);
  const [callUpdate, setCallUpdate] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<Set<string>>(new Set());

  const [callDate, setCallDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);

  const status = [
    { key: "Planned", label: "Planned" },
    { key: "Held", label: "Held" },
    { key: "Held - Staff Review", label: "Held - Staff Review" },
    { key: "Not Held", label: "Not Held" },
    { key: "Not Held - Left Message", label: "Not Held - Left Message" },
  ];

  const getData = async () => {
    setLoading(true);
    try {
      const result = await getCallData(callid!);
      setData(result);

      if (result?.call_details?.status) {
        setSelectedStatus(new Set([result.call_details.status]));
      }
    } catch (error) {
      console.error("Error fetching call data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (callid) {
      getData();
    }
  }, [callid]);

  const formattedComments = data?.comments
    ? data.comments.map((comment: string) => comment.trim()).join("\n\n")
    : "";

  const handleSubmit = async () => {
    const statusVal = Array.from(selectedStatus)[0];

    const payload = {
      call_id: callid,
      commentcontent: callUpdate.trim(),
      related_to: data?.call_details.contact_id || "",
      status: statusVal,
      date_of_call: callDate!.toISOString(),
      start_time: startTime!.toISOString(),
      end_time: endTime!.toISOString(),
    };

    setSubmitting(true);
    try {
      await postCallData(payload);
      setCallUpdate("");
      setCallDate(null);
      setStartTime(null);
      setEndTime(null);
      await getData();
    } catch (error) {
      console.error("Error submitting comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const getAnnualReviewInputColour = (rawDate?: string): Color => {
    if (!rawDate) {
      return "default"
    }

    const [day, month, year] = rawDate.split("/");
    const annualReviewDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    if (annualReviewDate < today) {
      return "danger";
    } else if (annualReviewDate <= thirtyDaysFromNow) {
      return "success";
    }
    return "default";
  }

  const getAnnualReviewInput = () => {
    const rawDate = data?.contact_details.annual_review_date;

    return (
      <Input
        label="Annual Review"
        isReadOnly
        value={rawDate || 'Not Specified'}
        color={getAnnualReviewInputColour(rawDate)}
      />
    );
  };

  const formatAddress = (): string => {
    const address = data?.contact_details.address || []
    return address.reduce((acc, line) =>  acc + (acc === '' ? '' : '\n') + line, '')
  }

  const isFormValid =
    callUpdate.trim() &&
    callDate &&
    startTime &&
    endTime &&
    Array.from(selectedStatus).length > 0;

  return (
    <div className="flex flex-col min-h-screen bg-white relative">
      <Navbar />
      <div className="w-full min-h-[3px] bg-yorkOrange"></div>
      {(loading || submitting) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <Spinner size="lg" color="primary" />
        </div>
      )}

      <main className="flex-1 container max-w-screen-lg mx-auto bg-white">
        <div className="mb-4 pt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Input label="Name" isReadOnly value={data?.contact_details.name || ""} />
          <Input label="Preferred Name" isReadOnly value={data?.contact_details.preferred_name || ""} />
          <Input label="Birthday" isReadOnly value={data?.contact_details.birthday || ""} />
          <Input label="Phone Number" isReadOnly value={data?.contact_details.homephone || ""} />
          <Textarea label="Address" isReadOnly value={formatAddress()} />
          <Input label="Mobile Number" isReadOnly value={data?.contact_details.mobile || ""} />
          {getAnnualReviewInput()}
        </div>

        <Divider className="mt-4 mb-4" />

        <div className="flex w-full flex-col">
          <Tabs aria-label="Options" variant="light" classNames={{tab: "bg-gray-100"}}>
            <Tab key="summary" title="Neighbour Summary">
              <Card>
                <Textarea
                  aria-label="Neighbour Summary"
                  labelPlacement="inside"
                  value={data?.contact_details.neighbour_summary || ""}
                  isReadOnly
                  minRows={12}
                  maxRows={12}
                />
              </Card>
            </Tab>
            <Tab key="callhistory" title="Previous Contact Summary">
              <Card>
                <Textarea
                  aria-label="Previous Call Summary"
                  labelPlacement="inside"
                  value={formattedComments || ""}
                  isReadOnly
                  minRows={12}
                  maxRows={12}
                />
              </Card>
            </Tab>
          </Tabs>
        </div>

        <Divider className="mt-4 mb-4" />

        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm">Status</p>
            <Select
              classNames={{trigger: "bg-white"}}
              color="default"
              size="md"
              variant="bordered"
              aria-label="Status"
              selectedKeys={selectedStatus}
              onSelectionChange={(keys) => setSelectedStatus(keys as Set<string>)}
            >
              {status.map((status) => (
                <SelectItem key={status.key}>{status.label}</SelectItem>
              ))}
            </Select>
          </div>

          <div>
            <p className="text-sm">Date of Call</p>
            <DatePicker
              format="DD/MM/YYYY"
              value={callDate}
              onChange={(date) => setCallDate(date?.toDate()|| null)}
              style={{ width: "100%", height: "39px", boxSizing: "border-box" }}
              containerStyle={{ width: "100%" }}
            />
          </div>

          <div>
            <p className="text-sm">Start Time of Call</p>
            <DatePicker
              disableDayPicker
              format="HH:mm"
              plugins={[<TimePicker hideSeconds />]}
              value={startTime}
              onChange={(time) => setStartTime(time?.toDate()|| null)}
              style={{ width: "100%", height: "39px", boxSizing: "border-box" }}
              containerStyle={{ width: "100%" }}
            />
          </div>

          <div>
            <p className="text-sm">End Time of Call</p>
            <DatePicker
              disableDayPicker
              format="HH:mm"
              plugins={[<TimePicker hideSeconds />]}
              value={endTime}
              onChange={(time) => setEndTime(time?.toDate() || null)}
              style={{ width: "100%", height: "39px", boxSizing: "border-box" }}
              containerStyle={{ width: "100%" }}
            />
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm">Call Update</p>
          <Textarea
            aria-label="Call Update"
            labelPlacement="inside"
            minRows={5}
            maxRows={15}
            value={callUpdate}
            onChange={(e) => setCallUpdate(e.target.value)}
          />
        </div>

        <div className="mb-4 flex justify-center">
          <Button
            className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600"
            onClick={handleSubmit}
            isDisabled={submitting || !isFormValid}
          >
            Submit
          </Button>
        </div>
      </main>
    </div>
  );
};

export default DataInput;
